import React, { useEffect } from 'react';
import Button from 'relements/build/esm/ui/button';
import Avatar from 'relements/build/esm/ui/avatar';
import Box from 'relements/build/esm/ui/box';
import Text from 'relements/build/esm/ui/text';
import BreadCrumb from '@src/components/BreadCrumb/BreadCrumb';
import { dispatch } from '@src/dispatcher';
import { Container } from '../styledComponents';
import { getDepartments, getTokens } from './actions';
import Departments from './Departments';
import Portal from './Portals';
import Brands from './Brands';
import LoadingState from './LoadingState';
import { getQueryParams } from './utils';

const commonProps = {
  padding: 'zero',
  marginY: 'lg',
  flexWrap: 'wrap',
};

let apiCalled = false;

function ZohoSetup({
  setRouteToZoho,
  navToHome,
  setZohoProps,
  zohoAuthResponse,
  showProductionError,
}) {
  const [activePage, setActivePage] = React.useState(0);
  const [authorized, setAuthorized] = React.useState(false);
  const [selectedDept, setSelectedDept] = React.useState([]);
  const [selectedBrand, setSelectedBrand] = React.useState('');
  const [selectedPortal, setSelectedPortal] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [departments, setDepartments] = React.useState([]);

  useEffect(() => {
    const params = getQueryParams();
    if (params?.zohoAuthorized === 'true' && zohoAuthResponse?.success) {
      if (params?.error !== 'access_denied') {
        if (!apiCalled) {
          apiCalled = true;
          setLoading(true);
          getTokens().then((res) => {
            if (res.response?.body?.success) {
              setAuthorized(true);
              setLoading(false);
            }
          });
        }
      }
    }
  }, []);

  useEffect(() => {
    if (zohoAuthResponse?.is_auth) {
      setAuthorized(true);
    }
  }, [zohoAuthResponse]);

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      const {
        response: { body },
      } = await getDepartments({
        screenName: selectedPortal,
        appId: selectedBrand,
      });
      setDepartments(body.data.departments);
      if (
        body.data.departments.filter(
          (dept) => Object.keys(dept).toString() === selectedDept[0]
        ).length === 0
      ) {
        setSelectedDept([]);
      }
      setLoading(false);
    }
    if (Pages[activePage].name === 'Departments') fetchData();
  }, [selectedBrand, activePage]);

  const Authorize = ({ onSuccess }) => {
    if (showProductionError) {
      return (
        <Box>
          <Text variant="body.md">
            Configure Zoho SalesIQ on our staging environment. Follow the steps
            below & head to staging -
            <ul>
              <li>
                Clicking on the drop-down (⋮) menu on the top right corner of
                the page.
              </li>
              <li>Select the Staging Enviornment from the list</li>
              <li>
                Get redirected to staging and complete Zoho SalesIQ
                configuration.
              </li>
            </ul>
          </Text>
        </Box>
      );
    }
    return loading ? (
      <LoadingState />
    ) : (
      <>
        <Text variants="body.md" color="haptik.black">
          Before configuring Zoho SalesIQ, you need to complete the
          authorization
        </Text>
        <Box {...commonProps}>
          <Button themed onClick={onSuccess}>
            Authorize
          </Button>
        </Box>
      </>
    );
  };

  const prevPage = () =>
    setActivePage(activePage - 1 < 0 ? activePage : activePage - 1);

  const nextPage = async () => {
    if (activePage === Pages.length - 1) {
      setZohoProps({
        selectedPortal,
        selectedBrand,
        selectedDept,
      });
      setRouteToZoho(false);
    } else {
      setActivePage(
        activePage + 1 > Pages.length - 1 ? activePage : activePage + 1
      );
    }
  };

  const setSelection = (key, value) => {
    if (key === 'portal') {
      setSelectedPortal(value);
    } else if (key === 'brands') {
      setSelectedBrand(value);
    } else {
      setSelectedDept(value);
    }
  };

  const onAuthoriseClick = async () => {
    if (zohoAuthResponse?.url) {
      const { origin, pathname } = window.location;
      const redirectBaseUrl = `${origin}${pathname}`;
      window.location.href = `${zohoAuthResponse.url}&redirect_uri=${redirectBaseUrl}?zohoAuthorized=true`;
    }
  };

  const Pages = [
    {
      name: 'Portal',
      component: Portal,
    },
    {
      name: 'Brands',
      component: Brands,
    },
    {
      name: 'Departments',
      component: Departments,
    },
  ];

  const checkNextBtnStatus = () => {
    if (loading) {
      return 'disabled';
    }
    switch (Pages[activePage].name) {
      case 'Portal':
        if (selectedPortal === '') {
          return 'disabled';
        }
        break;
      case 'Brands':
        if (selectedBrand === '') {
          return 'disabled';
        }
        break;
      case 'Departments':
        if (selectedDept.length === 0) {
          return 'disabled';
        }
        break;
      default:
        return 'primary';
    }
    return 'primary';
  };

  const renderPage = () => {
    const Component = Pages[activePage].component;
    return (
      <Component
        setSelection={setSelection}
        selectedBrand={selectedBrand}
        selectedPortal={selectedPortal}
        selectedDept={selectedDept}
        departments={departments}
        loading={loading}
      />
    );
  };

  return (
    <Container height="97%">
      <Box flexDirection="column" overflowY="overlay" flex="1">
        <BreadCrumb
          home="Agent Escalation"
          nextPage="Configure Zoho SalesIQ"
          marginBottom="16px"
          onClick={navToHome}
        />
        <Box
          padding={0}
          borderColor="grey.haptik"
          borderWidth={0}
          borderTopWidth={1}
        />
        <Box
          marginTop="30px"
          marginBottom="16px"
          padding="zero"
          alignItems="center"
        >
          <Avatar
            url="https://toolassets.haptikapi.com/platform/assets/zoho-salesiq.svg"
            initials="ZH"
            marginRight="md"
          />
          <Text variant="display-2.semi-bold">Configure Zoho SalesIQ</Text>
        </Box>
        {!authorized ? (
          <Authorize onSuccess={onAuthoriseClick} />
        ) : (
          renderPage()
        )}
      </Box>
      {authorized && (
        <Box
          justifyContent="flex-end"
          borderWidth="1px"
          borderColor="grey.haptik"
          zIndex={2}
          ml={-30}
        >
          {activePage !== 0 && (
            <Box {...commonProps} width="150px" flexDirection="column">
              <Button themed onClick={prevPage} variant="secondary">
                Back
              </Button>
            </Box>
          )}
          <Box {...commonProps} width="150px" flexDirection="column">
            <Button
              themed
              onClick={nextPage}
              variant={checkNextBtnStatus()}
              disabled={checkNextBtnStatus() === 'disabled'}
            >
              Next
            </Button>
          </Box>
        </Box>
      )}
    </Container>
  );
}

export default React.memo(ZohoSetup);
